import classNames from 'classnames'
import type { FC } from 'react'
import { useEffect } from 'react'
import { Button } from '~/components/Button/Button'
import { useCookies } from '~/features/cookies'
import type { CookiePreferenceOption } from '~/features/cookies/preferences'
import { cookiePreference } from '~/features/cookies/preferences'
import type { CookieBannerProps } from './CookieBanner.interface'
import styles from './CookieBanner.module.scss'
import { withHideOnVisualEditor } from '~/withHideOnStoryblok'

const CookieBanner: FC<CookieBannerProps> = ({
  title,
  text,
  acceptButton,
  policyButton,
  preferences,
  setPreference,
  closeBanner
}) => {
  const acceptCookies = (): void => {
    // Accept all preferences and hide the banner

    if (setPreference !== undefined) {
      preferences.forEach((name) => {
        setPreference(name, true)
      })
    }

    closeBanner?.()
  }

  return (
    <div id="cookiesBanner" className={styles.container}>
      <div className={styles.grid}>
        <div className={styles.body}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.text}>{text}</p>
        </div>
        <div className={styles.buttons}>
          <Button
            onClick={acceptCookies}
            // variant="inverted"
            className={classNames(styles.button, styles['primary-button'])}
          >
            {acceptButton.children}
          </Button>
          {policyButton != null && (
            <Button
              variant="secondary"
              link={policyButton.link}
              className={classNames(styles.button, styles['policy-button'])}
            >
              {policyButton.children}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export const CookieBannerContext: FC<CookieBannerProps> = ({
  title,
  text,
  acceptButton,
  policyButton,
  preferences
}) => {
  const {
    isBannerOpen,
    showBanner,
    syncPreferences,
    preferenceIsSet,
    setPreference
  } = useCookies()

  useEffect(() => {
    syncPreferences()

    // Show the banner if the necessary cookies are not yet set
    const necessaryCookie = Object.values(cookiePreference).find((value) => {
      const preference = value as CookiePreferenceOption

      return preference.isNecessary
    }) as CookiePreferenceOption

    const necessaryCookieExists = preferenceIsSet(necessaryCookie.name)

    showBanner(!necessaryCookieExists)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isBannerOpen) {
    return null
  }

  return (
    <CookieBanner
      title={title}
      text={text}
      acceptButton={acceptButton}
      policyButton={policyButton}
      preferences={preferences}
      setPreference={setPreference}
      closeBanner={() => { showBanner(false) }}
    />
  )
}

export const CookieBannerHiddenOnEditor =
  withHideOnVisualEditor(CookieBannerContext)
